import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import Footer from "@/components/Footer";
import AddressForm from "@/components/AddressForm";
import Logo from "@/components/Logo";
import {
  PackageCheck, Map as MapIcon, ShieldCheck, Wallet, ArrowRight, Sparkles, Truck, Bus, Leaf, Bike, Train, Calculator, Activity, Building2, Flame, Home, GraduationCap, FileText, Store, Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- Utilities ----------
const useCountUp = (target, duration = 1400) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!target) return;
    let raf, start = null;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / duration);
      setN(Math.floor(p * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return n;
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const Section = ({ children, className = "", testid }) => (
  <motion.section
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-80px" }}
    variants={fadeUp}
    className={className}
    data-testid={testid}
  >{children}</motion.section>
);

// ---------- Impact Counter ----------
const Counter = ({ value, suffix, label, icon: Icon }) => {
  const n = useCountUp(value);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-md transition-shadow" data-testid={`counter-${label}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500"><Icon className="w-3.5 h-3.5 text-amber-600"/> {label}</div>
      <div className="mt-2 font-display text-3xl sm:text-4xl font-extrabold text-slate-900">
        {n.toLocaleString("en-IN")}<span className="text-amber-600">{suffix || ""}</span>
      </div>
    </div>
  );
};

// ---------- Live carrier ticker (rotates messages) ----------
const HeroTicker = () => {
  const messages = [
    "Now launching across India",
    "Send with travellers already going your way",
    "Carry a parcel on your next trip and earn",
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setIdx((i) => (i + 1) % messages.length), 3200);
    return () => clearInterval(iv);
  }, [messages.length]);
  return (
    <div className="inline-flex items-center gap-2 rounded-full bg-white/90 backdrop-blur border border-slate-200 px-3 py-1.5 shadow-sm" data-testid="hero-ticker">
      <span className="relative flex w-2 h-2">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping"/>
        <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500"/>
      </span>
      <motion.span
        key={idx}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35 }}
        className="text-xs font-medium text-slate-700"
      >{messages[idx]}</motion.span>
    </div>
  );
};

// ---------- Interactive Price Estimator ----------
// Uses the exact same shared distance calc (_resolve_route via /api/public/geo/route)
// and the exact same shared pricer (price_for_carrier via /api/public/estimate) as the
// authenticated create-parcel + browse-trips flow. No second implementation.
const SIZE_TIERS = [
  { key: "envelope", title: "Envelope",  weight: "up to 0.5 kg",  example: "Documents and papers" },
  { key: "small",    title: "Small",     weight: "up to 2 kg",    example: "Fits in a backpack" },
  { key: "medium",   title: "Medium",    weight: "2 – 5 kg",       example: "Cabin bag sized" },
  { key: "large",    title: "Large",     weight: "5 – 15 kg",      example: "Large suitcase sized" },
  { key: "xl",       title: "Extra Large",        weight: "15 – 30 kg",     example: "Needs a car boot" },
];

const Estimator = () => {
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [mode, setMode] = useState("car");
  const [size, setSize] = useState("small");
  const [estimate, setEstimate] = useState(null);
  const [state, setState] = useState({ loading: false, error: "" });
  const cacheRef = useRef(new Map());

  useEffect(() => {
    if (!from?.lat || !to?.lat) return;
    const key = `${mode}|${size}|${from.lat.toFixed(4)},${from.lng.toFixed(4)}|${to.lat.toFixed(4)},${to.lng.toFixed(4)}`;
    if (cacheRef.current.has(key)) { setEstimate(cacheRef.current.get(key)); return; }
    setState({ loading: true, error: "" });
    axios.post(`${API}/public/estimate`, {
      travel_mode: mode, size,
      from_lat: from.lat, from_lng: from.lng,
      to_lat: to.lat, to_lng: to.lng,
    })
      .then((r) => {
        cacheRef.current.set(key, r.data);
        setEstimate(r.data);
        setState({ loading: false, error: "" });
      })
      .catch(() => {
        setEstimate(null);
        setState({ loading: false, error: "Sorry — we couldn't calculate this route right now. Try different addresses or refresh the page." });
      });
  }, [from?.lat, from?.lng, to?.lat, to?.lng, mode, size]);

  const ready = !!from && !!to;

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="estimator-card">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl bg-amber-100 grid place-items-center"><Calculator className="w-4 h-4 text-amber-700"/></div>
        <div>
          <div className="font-display font-bold text-lg">Try the price estimator</div>
          <div className="text-xs text-slate-500">Indicative — final price depends on the carrier you pick</div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <AddressForm label="From" value={from} onChange={setFrom} allowCurrentLocation testid="est-from"/>
        <AddressForm label="To" value={to} onChange={setTo} testid="est-to"/>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500">Transport mode</label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger data-testid="est-mode" className="rounded-xl mt-1"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="car">Self-driven car</SelectItem>
              <SelectItem value="bike">Self-driven bike / scooter</SelectItem>
              <SelectItem value="suv">Self-driven SUV / tempo</SelectItem>
              <SelectItem value="train">Train (pre-booked)</SelectItem>
              <SelectItem value="bus">Bus (pre-booked)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500">Parcel size</label>
          <Select value={size} onValueChange={setSize}>
            <SelectTrigger data-testid="est-size" className="rounded-xl mt-1"><SelectValue/></SelectTrigger>
            <SelectContent>
              {SIZE_TIERS.map(t => (
                <SelectItem key={t.key} value={t.key}>{t.title} · {t.weight} — {t.example}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-5 rounded-2xl bg-slate-900 text-white p-5" data-testid="est-result">
        {!ready && (
          <div className="text-sm text-slate-300" data-testid="est-empty">Pick a From and To address to see your indicative price.</div>
        )}
        {ready && state.loading && (
          <div className="flex items-center gap-2 text-sm text-slate-300" data-testid="est-loading">
            <Loader2 className="w-4 h-4 animate-spin text-amber-300"/> Calculating driving distance and price…
          </div>
        )}
        {ready && state.error && (
          <div className="text-sm text-rose-300" data-testid="est-error">{state.error}</div>
        )}
        {ready && !state.loading && !state.error && estimate && (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-amber-300">Indicative all-inclusive price</div>
              <div className="mt-1 font-display text-4xl font-extrabold" data-testid="est-total">
                ₹{estimate.sender_total.toFixed(0)}
              </div>
              <div className="text-xs text-slate-400 mt-1" data-testid="est-route">
                {estimate.distance_km} km by road · ~{Math.round(estimate.duration_min)} min · {mode.toUpperCase()}
              </div>
              <div className="text-[11px] text-slate-500 mt-1 max-w-md leading-snug">
                Final price depends on which carrier accepts and their exact detour. Sign up to see real quotes.
              </div>
            </div>
            <Link to="/login" data-testid="est-cta">
              <Button className="rounded-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">Send now <ArrowRight className="w-4 h-4 ml-1"/></Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Landing ----------
const Landing = () => {
  const examples = [
    { icon: Truck,  tone: "amber",   label: "Intra-city · self-driven",       title: "Delhi → Gurugram · by car · ~30 km",           left: "Fuel cost: ~₹200",       right: "Carrying a parcel: ~₹100–150", note: "Covers roughly half your fuel cost for the trip." },
    { icon: Truck,  tone: "amber",   label: "Intercity · self-driven",        title: "Delhi → Chandigarh · by car · ~250 km",        left: "Fuel cost: ~₹1,600–1,800", right: "Carrying a parcel: ~₹450–550", note: "Offsets a meaningful share of your fuel cost." },
    { icon: Bus,    tone: "blue",    label: "Intercity · pre-booked",         title: "Delhi → Chandigarh · by AC bus · ~250 km",      left: "Ticket cost: ~₹500–600", right: "Carrying a parcel: ~₹250–300", note: "Covers about half your ticket price." },
    { icon: Train,  tone: "indigo",  label: "Intercity · pre-booked",         title: "Delhi → Mumbai · by AC train · ~1,150 km",       left: "Ticket cost: ~₹1,800–2,500", right: "Carrying a parcel: ~₹700–900", note: "A solid contribution toward your ticket, more with multiple parcels." },
  ];
  const toneMap = {
    amber:  { border: "border-amber-200",  bg: "bg-amber-50/60",  chip: "bg-amber-100 text-amber-700",  tag: "text-amber-700" },
    blue:   { border: "border-blue-200",   bg: "bg-blue-50/60",   chip: "bg-blue-100 text-blue-700",   tag: "text-blue-700" },
    indigo: { border: "border-indigo-200", bg: "bg-indigo-50/60", chip: "bg-indigo-100 text-indigo-700", tag: "text-indigo-700" },
  };

  return (
    <div className="min-h-screen bg-slate-50 grid-bg">
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="landing-home-link">
          <Logo size={34} variant="dark"/>
          <span className="font-display text-xl font-bold">CarryIt</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/login">
            <Button data-testid="get-started-btn" className="rounded-full bg-amber-600 hover:bg-amber-700">Get started <ArrowRight className="w-4 h-4 ml-1"/></Button>
          </Link>
        </div>
      </header>

      {/* HERO */}
      <Section testid="hero-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-24 sm:pb-32 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <HeroTicker/>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Send parcels with travellers <span className="text-amber-600">already heading there.</span>
          </h1>
          <p className="mt-5 text-slate-600 text-lg max-w-lg">
            CarryIt matches your parcel with verified carriers on trains, buses, and road trips. The carrier is paid only after you confirm delivery with a PIN.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/login"><Button data-testid="hero-send-btn" size="lg" className="rounded-full bg-slate-900 hover:bg-slate-800">Send a parcel</Button></Link>
            <Link to="/login"><Button data-testid="hero-earn-btn" size="lg" variant="outline" className="rounded-full border-slate-300">Earn as a carrier</Button></Link>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600"/> KYC verified</div>
            <div className="flex items-center gap-2"><Wallet className="w-4 h-4 text-amber-600"/> Paid on delivery PIN</div>
            <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-600"/> Trust tiers</div>
          </div>
        </div>
        <div className="relative">
          <img src="https://images.unsplash.com/photo-1659353741091-e0274bb50905?w=940&h=1180&fit=crop&crop=faces&auto=format" alt="Happy Indian delivery carrier smiling with parcels" className="rounded-3xl object-cover aspect-[4/5] w-full shadow-2xl"/>
          <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl border border-slate-100 w-64 hidden sm:block">
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-1"><Train className="w-3 h-3"/> Delhi → Bengaluru · Thu</div>
            <div className="font-semibold">Rohan · Tier 2</div>
            <div className="mt-2 text-2xl font-display font-extrabold">₹ 380</div>
            <div className="text-xs text-slate-500">All inclusive</div>
          </div>
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section testid="how-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-28">
        <h2 className="font-display text-3xl sm:text-4xl font-bold mb-3">How it works</h2>
        <p className="text-slate-500 text-sm mb-10 max-w-2xl">Three simple steps. The carrier is paid only after delivery is confirmed.</p>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {icon: PackageCheck, t:"Post your parcel", d:"Pickup, drop, size, and a photo. We calculate one all-in price."},
            {icon: MapIcon, t:"Match a traveller", d:"Choose from verified carriers going your way. Pay securely online or in cash."},
            {icon: ShieldCheck, t:"PIN-verified handoff", d:"Pickup and drop PINs confirm each handoff. The carrier is paid only after delivery."},
          ].map((f, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all">
              <div className="w-10 h-10 rounded-xl bg-amber-100 grid place-items-center mb-3"><f.icon className="w-5 h-5 text-amber-700" strokeWidth={2}/></div>
              <div className="font-display font-semibold text-lg">{f.t}</div>
              <div className="text-slate-600 mt-1 text-sm">{f.d}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* WHY CARRYIT */}
      <Section testid="why-carryit-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-28">
        <h2 className="font-display text-3xl sm:text-4xl font-bold mb-3">Why CarryIt</h2>
        <p className="text-slate-500 text-sm mb-10 max-w-2xl">A single network that saves money for senders, adds income for travellers, and puts fewer trucks on the road.</p>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            {icon: Sparkles, tag:"For senders", t:"Cheaper than couriers", d:"One transparent price, no fuel surcharges, no last-mile mystery fees. Send between cities for a fraction of a courier's rate."},
            {icon: Wallet, tag:"For carriers", t:"Turn a trip into income", d:"You're already on that train, bus or highway. Carry a parcel and offset your ticket or fuel — with instant UPI payouts."},
            {icon: Leaf, tag:"For the planet", t:"One fewer truck on the road", d:"Every CarryIt handoff piggybacks on a journey that was already happening. Less duplication, lower emissions, real climate impact."},
          ].map((b, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all" data-testid={`why-col-${i}`}>
              <div className="w-10 h-10 rounded-xl bg-slate-900 grid place-items-center mb-3"><b.icon className="w-5 h-5 text-amber-400" strokeWidth={2}/></div>
              <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">{b.tag}</div>
              <div className="font-display font-semibold text-lg mt-1">{b.t}</div>
              <div className="text-slate-600 mt-1 text-sm">{b.d}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* WHO CARRYIT IS FOR */}
      <Section testid="who-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-28">
        <h2 className="font-display text-3xl sm:text-4xl font-bold mb-3">One platform, every kind of parcel</h2>
        <p className="text-slate-500 text-sm mb-10 max-w-2xl">From something you forgot at home to your next business shipment.</p>

        <div className="grid sm:grid-cols-2 gap-6">
          {[
            { icon: Home,          tag: "Household & personal", t: "Forgot something? Sending a gift?", d: "From a laptop left at a family member's house to home-cooked food for someone in another city — CarryIt gets personal items where they need to go, fast." },
            { icon: GraduationCap, tag: "Students",             t: "Between hometown and college",       d: "Send or receive items with family without waiting days for a courier — perfect for the back-and-forth of student life." },
            { icon: FileText,      tag: "Documents & pros",     t: "Urgent paperwork, sorted",           d: "Contracts, certificates, signed documents — when something needs to reach another city today, not in 2–3 days." },
            { icon: Store,         tag: "Small businesses",     t: "Built for small businesses too",     d: "Send product samples, small inventory batches, or customer orders between cities at a fraction of standard courier cost — ideal for D2C sellers and local businesses." },
          ].map((u, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all" data-testid={`who-card-${i}`}>
              <div className="w-10 h-10 rounded-xl bg-amber-100 grid place-items-center mb-3">
                <u.icon className="w-5 h-5 text-amber-700" strokeWidth={2}/>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">{u.tag}</div>
              <div className="font-display font-semibold text-lg mt-1">{u.t}</div>
              <div className="text-slate-600 mt-1 text-sm">{u.d}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* INTERACTIVE ESTIMATOR */}
      <Section testid="estimator-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-28">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mb-3">Price it before you post</h2>
            <p className="text-slate-500 text-sm mb-4 max-w-lg">Slide the distance, pick a mode and size — CarryIt shows the exact all-inclusive price the sender would pay. No sign-up needed.</p>
            <div className="flex items-center gap-2 text-xs text-slate-500"><ShieldCheck className="w-4 h-4 text-emerald-600"/> Same engine used inside the app.</div>
          </div>
          <Estimator/>
        </div>
      </Section>

      {/* TRUST BAR */}
      <Section testid="trust-bar" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 items-center">
            <div className="flex items-center gap-2 text-sm text-slate-700"><ShieldCheck className="w-4 h-4 text-emerald-600"/> KYC verified</div>
            <div className="flex items-center gap-2 text-sm text-slate-700"><Wallet className="w-4 h-4 text-amber-600"/> Paid on delivery PIN</div>
            <div className="flex items-center gap-2 text-sm text-slate-700"><Building2 className="w-4 h-4 text-slate-500"/> Launching soon in your city</div>
            <div className="flex items-center gap-2 text-sm text-slate-700"><Sparkles className="w-4 h-4 text-amber-600"/> 4-tier trust system</div>
          </div>
        </div>
      </Section>

      <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 text-center text-xs text-slate-400">
        © {new Date().getFullYear()} CarryIt · Made in India
      </footer>
      <Footer/>
    </div>
  );
};

export default Landing;
